import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff } from "@fortawesome/free-solid-svg-icons";

import styles from "./uptime_counter.module.css";
import { pduiIsReady } from "../../utils/utils";

// Re-anchor the local clock only when the server value drifts more than this
// from what is already on screen: absorbs poll/network jitter, but a reboot
// (hours of difference) snaps immediately.
const RESYNC_THRESHOLD_MS = 2000;

const pad2 = (n) => String(n).padStart(2, "0");

export function formatUptime(totalSeconds, daysUnit = "d") {
  if (typeof totalSeconds !== "number" || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return null;
  }
  const s = Math.floor(totalSeconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const clock = `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
  return days > 0 ? `${days}${daysUnit} ${clock}` : clock;
}

/**
 * Time since the device booted, as reported by its heartbeat.
 *
 * - Online: ticks every second from the server-computed `uptime_seconds`,
 *   so the browser clock never matters.
 * - Offline: frozen at the uptime of the last heartbeat
 *   (`last_heartbeat - booted_at`, both server times), because we cannot know
 *   whether the device is still powered.
 * - No `booted_at`: the device runs firmware that does not report uptime yet.
 */
export default function UptimeCounter({ pduiData }) {
  const { t } = useTranslation();
  const bootedAt = pduiData?.booted_at || null;
  const lastHeartbeat = pduiData?.last_heartbeat || null;
  const serverUptime = pduiData?.uptime_seconds;
  const online = !!lastHeartbeat && pduiIsReady(lastHeartbeat);

  const [anchor, setAnchor] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const lastBootedAt = useRef(bootedAt);

  useEffect(() => {
    const bootChanged = lastBootedAt.current !== bootedAt;
    lastBootedAt.current = bootedAt;
    if (typeof serverUptime !== "number" || !Number.isFinite(serverUptime)) {
      setAnchor(null);
      return;
    }
    const next = Date.now() - serverUptime * 1000;
    setAnchor((prev) =>
      prev === null || bootChanged || Math.abs(next - prev) > RESYNC_THRESHOLD_MS ? next : prev
    );
    setNow(Date.now());
  }, [serverUptime, bootedAt]);

  useEffect(() => {
    if (!online) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [online]);

  let seconds = null;
  let subtitle = t("detail.uptime.noData");
  if (bootedAt) {
    if (online && anchor !== null) {
      seconds = Math.max(0, (now - anchor) / 1000);
      subtitle = t("detail.uptime.since");
    } else if (lastHeartbeat) {
      const frozen = (new Date(lastHeartbeat).getTime() - new Date(bootedAt).getTime()) / 1000;
      seconds = Number.isFinite(frozen) ? Math.max(0, frozen) : null;
      subtitle = t("detail.uptime.lastReport");
    }
  }

  const text = formatUptime(seconds, t("detail.uptime.daysShort"));
  const live = online && text !== null;

  return (
    <div className={`${styles.tile} ${live ? "" : styles.stale}`} data-testid="uptime-counter">
      <div className={styles.icon}>
        <FontAwesomeIcon icon={faPowerOff} />
      </div>
      <div className={styles.body}>
        <div className={styles.label}>{t("detail.uptime.label")}</div>
        <div className={styles.value}>{text || "--:--:--"}</div>
        <div className={styles.sub}>{subtitle}</div>
      </div>
    </div>
  );
}
